import { type PaletteMode, type Theme, ThemeProvider } from "@mui/material";
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useMemo,
	useState,
} from "react";
import { themeStorageKey } from "web_myplann/Utils/WebViewModel";

export type GetThemeFunctionType = (
	variant: PaletteMode,
	hspColor?: string,
	zIndexBase?: number,
) => Theme;

export type ColorSchemeOptions = {
	paletteMode: PaletteMode;
	hspColor: string | undefined;
};

type SetModeOptions = {
	persist?: boolean;
};

type ThemeModeContextProps = {
	mode: ColorSchemeOptions;
	setMode: (
		variant: Partial<ColorSchemeOptions>,
		options?: SetModeOptions,
	) => void;
};

const hspColorKey = "hspColor";

const getPreferredTheme = (): PaletteMode => {
	const storageValue = localStorage.getItem(themeStorageKey);
	if (storageValue) return storageValue === "dark" ? "dark" : "light";
	return window?.matchMedia("(prefers-color-scheme: dark)")?.matches
		? "dark"
		: "light";
};

const getHspColor = () => localStorage.getItem(hspColorKey) ?? undefined;

const ThemeModeContext = createContext<ThemeModeContextProps>(
	{} as ThemeModeContextProps,
);

const ColorSchemeProvider = ({
	children,
	getTheme,
	zIndexBase,
	defaultPaletteMode,
}: {
	children: ReactNode;
	getTheme: GetThemeFunctionType;
	zIndexBase?: number;
	defaultPaletteMode?: PaletteMode;
}) => {
	const [mode, setMode] = useState<ColorSchemeOptions>({
		paletteMode: defaultPaletteMode ?? getPreferredTheme(),
		hspColor: getHspColor(),
	});

	const setColorScheme = useCallback(
		(variant: Partial<ColorSchemeOptions>, options?: SetModeOptions) => {
			if (options?.persist !== false) {
				if (variant.hspColor)
					localStorage.setItem(hspColorKey, variant.hspColor);
				if (variant.paletteMode)
					localStorage.setItem(themeStorageKey, variant.paletteMode);
			}
			setMode((a) => ({ ...a, ...variant }));
		},
		[],
	);

	const theme = useMemo(
		() => getTheme(mode.paletteMode, mode.hspColor, zIndexBase),
		[mode.hspColor, mode.paletteMode, getTheme, zIndexBase],
	);

	const contextValue = useMemo(
		() => ({ mode, setMode: setColorScheme }),
		[mode, setColorScheme],
	);

	return (
		<ThemeModeContext.Provider value={contextValue}>
			<ThemeProvider theme={theme}>{children}</ThemeProvider>
		</ThemeModeContext.Provider>
	);
};

export default ColorSchemeProvider;

export const useColorScheme = () => useContext(ThemeModeContext);
